import { useCallback, useMemo, useRef, useState } from "react";
import apiClient from "../tools/axiosConfig";
import {
  validateFile, validateFolderSize, formatUploadError, buildImportFormData, LONG_REQUEST_TIMEOUT_MS,
  BUNDLE_IMPORT_ACCEPT, BUNDLE_IMPORT_ACCEPTED_TYPES, BUNDLE_IMPORT_MAX_MB,
} from "../utils/fileUploadHelpers";
import { formatImportAnswer } from "../utils/importExportMessages";
import useLongAction, { actionFailure } from "../components/shared/long-action/useLongAction";
import DependencyResolutionDialog from "../components/admin/course/edit-scenario/DependencyResolutionDialog";
import { previewRequiresUserInput } from "../utils/reflectionScenarioOptions";

const KINDS = {
  lesson: { path: "lesson", label: "Lesson", key: "lesson_name", preview: true },
  scenario: { path: "scenario", label: "Scenario", key: "scenario_name", preview: true },
  infoPage: { path: "info-page", label: "Info page", key: "info_page_name", preview: true },
};

/**
 * The form of a bundle import (a unit, lesson, role play or info page) from `source`, `{ kind: "file", file }` or
 * `{ kind: "folder", files }`. The preview sends no choices; the import sends the dependency dialog's
 * `dependencies`, and `content` (`{ "<ref>": { "scenario_id": N } }`) when a reflection lesson reads a role play
 * that the bundle does not carry.
 */
export const buildBundleImportFormData = (source, dependencies, content) => buildImportFormData(
  source.kind === "folder" ? source.files : source.file,
  {
    ...(dependencies != null ? { dependency_resolutions: JSON.stringify(dependencies) } : {}),
    ...(content != null ? { content_resolutions: JSON.stringify(content) } : {}),
  },
);

export function useUnitTreeImport({ showError, onRefreshCourse }) {
  const longAction = useLongAction();
  const lessonInputRef = useRef(null);
  const lessonFolderInputRef = useRef(null);
  const scenarioInputRef = useRef(null);
  const scenarioFolderInputRef = useRef(null);
  const infoPageInputRef = useRef(null);
  const infoPageFolderInputRef = useRef(null);
  const activeUnitIdRef = useRef(null);
  const [busyKind, setBusyKind] = useState(null);
  const [pending, setPending] = useState(null);

  const performImport = useCallback(async (dependencies, content, override = null) => {
    const operation = override || pending;
    if (!operation) return;
    const { kind, source, unitId } = operation;
    const { path, label } = KINDS[kind];
    const inDialog = Boolean(pending && !override);
    const failure = (error) => formatUploadError(error, `Failed to import ${label.toLowerCase()}`);
    setBusyKind(kind);
    try {
      const task = async () => {
        const response = await apiClient.post(
          `/backoffice/v1/units/${unitId}/import-${path}/`,
          buildBundleImportFormData(source, dependencies || {}, content || {}),
          { headers: { "Content-Type": "multipart/form-data" }, timeout: LONG_REQUEST_TIMEOUT_MS }
        );
        await onRefreshCourse?.();
        return response;
      };
      if (inDialog) {
        const response = await task();
        setPending(null);
        longAction.showAnswer({
          message: formatImportAnswer(response.data, `${label} imported.`),
        });
      } else {
        await longAction.run({
          label: `Importing the ${label.toLowerCase()}…`, task, failure,
          success: (response) => formatImportAnswer(response.data, `${label} imported.`),
        });
      }
    } catch (error) {
      if (inDialog) longAction.showAnswer(actionFailure(error, failure));
    } finally {
      setPending(null);
      setBusyKind(null);
    }
  }, [longAction, onRefreshCourse, pending]);

  const submit = useCallback(async (kind, source, unitId) => {
    const operation = { kind, source, unitId };
    const { path, label, preview } = KINDS[kind];
    if (!preview) return performImport({}, {}, operation);
    setBusyKind(kind);
    try {
      const response = await longAction.run({
        label: `Checking the ${label.toLowerCase()} import…`,
        task: () => apiClient.post(
          `/backoffice/v1/units/${unitId}/preview-${path}-import/`,
          buildBundleImportFormData(source, null, null),
          { headers: { "Content-Type": "multipart/form-data" }, timeout: LONG_REQUEST_TIMEOUT_MS }
        ),
        success: () => null,
        failure: (error) => formatUploadError(error, `Couldn't preview ${label.toLowerCase()} import`),
      });
      if (!response) return;
      if (previewRequiresUserInput(response.data)) {
        setPending({ ...operation, preview: response.data });
      } else {
        await performImport({}, {}, operation);
      }
    } catch (_error) {
      // The long-action panel shows the failure.
    } finally {
      setBusyKind(null);
    }
  }, [longAction, performImport]);

  const onChange = (kind, folder) => async (event) => {
    const unitId = activeUnitIdRef.current;
    activeUnitIdRef.current = null;
    try {
      const files = Array.from(event.target.files || []);
      if (!unitId || !files.length) return;
      if (!folder) {
        const validation = validateFile(files[0], {
          maxSizeMB: BUNDLE_IMPORT_MAX_MB, acceptedTypes: BUNDLE_IMPORT_ACCEPTED_TYPES,
        });
        if (!validation.valid) { showError(validation.error); return; }
      } else {
        const validation = validateFolderSize(files, BUNDLE_IMPORT_MAX_MB);
        if (!validation.valid) { showError(validation.error); return; }
      }
      await submit(kind, folder ? { kind: "folder", files } : { kind: "file", file: files[0] }, unitId);
    } finally { event.target.value = ""; }
  };

  const unitImportHandlers = useMemo(() => {
    const picker = (ref) => (unitId) => {
      activeUnitIdRef.current = unitId;
      ref.current?.click();
    };
    return {
      onImportLesson: picker(lessonInputRef),
      onImportLessonFolder: picker(lessonFolderInputRef),
      onImportScenario: picker(scenarioInputRef),
      onImportScenarioFolder: picker(scenarioFolderInputRef),
      onImportInfoPage: picker(infoPageInputRef),
      onImportInfoPageFolder: picker(infoPageFolderInputRef),
    };
  }, []);

  const input = (kind, ref, folder = false) => (
    <input ref={ref} type="file" aria-label={`${KINDS[kind].label} import ${folder ? "folder" : "file"}`} onChange={onChange(kind, folder)}
      {...(folder ? { webkitdirectory: "", directory: "", multiple: true } : { accept: BUNDLE_IMPORT_ACCEPT })}
      style={{ display: "none" }} tabIndex={-1} aria-hidden="true" disabled={busyKind === kind} />
  );
  const importPortal = <>
    {input("lesson", lessonInputRef)}
    {input("lesson", lessonFolderInputRef, true)}
    {input("scenario", scenarioInputRef)}
    {input("scenario", scenarioFolderInputRef, true)}
    {input("infoPage", infoPageInputRef)}
    {input("infoPage", infoPageFolderInputRef, true)}
    <DependencyResolutionDialog open={Boolean(pending)}
      onClose={() => setPending(null)} onConfirm={performImport} previewData={pending?.preview}
      loading={Boolean(busyKind)} entityLabel={KINDS[pending?.kind]?.label || "Scenario"}
      entityNameKey={KINDS[pending?.kind]?.key || "scenario_name"} />
  </>;
  return { unitImportHandlers, importPortal };
}
